import { type NodeChange } from '@xyflow/react';
import { useEffect, useRef } from 'react';

import { useChatComposerStore } from '@air/feature-ag-ui-chat';

import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';

export type UseEscShortcutParams = {
  isFullscreen?: boolean;
  onCloseCanvas?: () => void;
  onExitFullscreen?: () => void;
};

export function useEscShortcut({ isFullscreen, onCloseCanvas, onExitFullscreen }: UseEscShortcutParams) {
  const canvasMode = useEditCanvasStore((state) => state.canvasMode);

  // Track whether nodes were selected BEFORE ReactFlow's Escape handler deselects them.
  // ReactFlow handles Escape on the node element (bubbling phase), which fires before
  // document-level listeners. By the time a document keydown handler runs, the store
  // already has no selected nodes. A capturing-phase listener on document fires first.
  const hadSelectedNodesRef = useRef(false);
  /** If true at capture, chat composer @ or / suggestions were open — do not close canvas on Escape. */
  const hadChatSuggestionRef = useRef(false);

  useEffect(() => {
    if (canvasMode.mode !== 'default' || !(onCloseCanvas || onExitFullscreen)) {
      return;
    }

    const handleCapture = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && e.isTrusted) {
        hadSelectedNodesRef.current = useEditCanvasStore.getState().nodes.some((n) => n.selected);
        const { chatMentionSuggestionOpen, chatSlashCommandSuggestionOpen } = useChatComposerStore.getState();
        hadChatSuggestionRef.current = chatMentionSuggestionOpen || chatSlashCommandSuggestionOpen;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !e.isTrusted) {
        return;
      }

      if (hadChatSuggestionRef.current) {
        hadChatSuggestionRef.current = false;
        // Let suggestion plugin + composer handle Escape; do not close canvas or fullscreen
        return;
      }

      if (hadSelectedNodesRef.current) {
        hadSelectedNodesRef.current = false;

        // ReactFlow only deselects nodes when the node element has DOM focus.
        // Nodes selected via the store (not focus) must be deselected manually.
        const { nodes } = useEditCanvasStore.getState();
        const stillSelected = nodes.filter((n) => n.selected);

        if (stillSelected.length > 0) {
          useEditCanvasStore
            .getState()
            .onNodesChange(
              stillSelected.map((n): NodeChange<AppNode> => ({ id: n.id, selected: false, type: 'select' })),
            );
        }

        return;
      }

      e.preventDefault();
      isFullscreen ? onExitFullscreen?.() : onCloseCanvas?.();
    };

    // window capture runs before document capture (e.g. Radix DismissableLayer), so we snapshot
    // chat suggestion flags before exitSuggestion/onExit clears the store.
    globalThis.window.addEventListener('keydown', handleCapture, true);
    // Bubble phase: decide whether to close/exit
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      globalThis.window.removeEventListener('keydown', handleCapture, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvasMode.mode, isFullscreen, onCloseCanvas, onExitFullscreen]);
}
